import React, { useState, useEffect } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CircularProgress, Pagination, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useGetCategoriesProductsQuery, useLazyGetAllSubCategoryByIdCategoryQuery } from "../apis/CategoriesApi";
import { useDeleteReturnVendorProductsMutation, useGetPaginatedReturnVendorProductsQuery } from "../apis/ReturnProductStoreApi";
import { setProducts, setSelectedProduct } from "../common/slices/productSlice";
import { debounce } from "lodash"; // Import debounce from lodash
import AccountAmazon from './AccountAmazon';

const ReturnedProductList = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
 // Mutation for deleting a product
 const [deleteProduct, { isLoading: isDeleting }] = useDeleteReturnVendorProductsMutation();

  // Initialize state from URL parameters
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialSearch = searchParams.get('search') || '';
  const initialCategory = searchParams.get('category') || '';
  const initialSubcategory = searchParams.get('subcategory') || '';
  const sortParam = searchParams.get('sort') || 'id,desc';
  const [initialSortField, initialSortOrder] = sortParam.split(',');

  // State management
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedSubcategory, setSelectedSubcategory] = useState(initialSubcategory);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [sortField, setSortField] = useState(initialSortField);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);

  // Fetch categories and subcategories
  const { data: categories } = useGetCategoriesProductsQuery();
  const [fetchSubcategories, { data: subcategories }] = useLazyGetAllSubCategoryByIdCategoryQuery();

  const itemsPerPage = 24;
  const apiPage = currentPage - 1;

  // API call with dynamic query
  const { data, error, isLoading } = useGetPaginatedReturnVendorProductsQuery({
    page: apiPage,
    size: itemsPerPage,
    sort: [sortField, sortOrder],
    productName: searchTerm,
    categoryId: selectedCategory,
    subCategoryId: selectedSubcategory
  });

   // Debounced search function
  const debouncedSearch = debounce((term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to the first page when searching
  }, 300); // 300ms delay

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    debouncedSearch(term);
  };

  // Sync state with URL parameters
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage);
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
    params.set('sort', `${sortField},${sortOrder}`);
    setSearchParams(params, { replace: true });
  }, [currentPage, searchTerm, selectedCategory, selectedSubcategory, sortField, sortOrder, setSearchParams]);
 
  // Reset subcategory when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories(selectedCategory);
      if (initialSubcategory === "") {
        setSelectedSubcategory("");
      }
    }
  }, [selectedCategory, fetchSubcategories, initialSubcategory]);

  // Dispatch products to Redux when API data changes
  useEffect(() => {
    if (data?.returnVendorProducts) {
      dispatch(setProducts(data.returnVendorProducts));
    }
  }, [data, dispatch]);

  if (isLoading)
    return (
      <div className="flex-center py-20" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );

  if (error) return <div>Error loading products</div>;

  const returnVendorProducts = data?.returnVendorProducts || [];
  const totalPages = data?.totalPages || 1;
 
  const handleDelete = async (productId) => {
    try {
      await deleteProduct(productId).unwrap(); // Perform the delete operation
      alert(`Product has been removed successfully!`); // Success notification
    } catch (error) {
      console.error(`Failed to delete product with ID ${productId}:`, error);
      alert(`Failed to remove product with ID ${productId}. Please try again.`); // Error notification
    }
  };
  return (
    <div className="product mt-24">
      <div className="container container-lg">
        {/* Top Search and Filters */}
        <div className='flex-between flex-wrap gap-8 mb-40'>
          <form className='search-form__wrapper position-relative d-block' onSubmit={(e) => e.preventDefault()}>
            <input
              type='text'
              className='search-form__input common-input py-13 ps-16 pe-18 rounded-pill pe-44'
              placeholder='Search by product...'
              defaultValue={searchTerm} // Use defaultValue instead of value
              onChange={handleSearchChange} // Use debounced handler
            />
            <button type='submit' className='w-32 h-32 bg-main-600 rounded-circle flex-center text-xl text-white position-absolute top-50 translate-middle-y inset-inline-end-0 me-8'>
              <i className='ph ph-magnifying-glass' />
            </button>
          </form>

          <div className='flex-align gap-16 mb-5 mt-5'>
            <FormControl variant="outlined" sx={{ minWidth: 180 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories?.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" sx={{ minWidth: 180 }} disabled={!selectedCategory}>
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                label="Subcategory"
              >
                <MenuItem value="">All Subcategories</MenuItem>
                {subcategories?.map(subcategory => (
                  <MenuItem key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        {/* <div className='flex-align gap-16'>
  {/* Sort Controls */}
  {/* <div className='flex-align gap-8'>
    <FormControl variant="outlined" sx={{ minWidth: 120 }}>
      <InputLabel>Sort Field</InputLabel>
      <Select
        value={sortField}
        onChange={(e) => setSortField(e.target.value)}
        label="Sort Field"
      >
        <MenuItem value="id">ID</MenuItem>
        <MenuItem value="name">Name</MenuItem>
      </Select>
    </FormControl>
    <FormControl variant="outlined" sx={{ minWidth: 140 }}>
      <InputLabel>Sort Order</InputLabel>
      <Select
        value={sortOrder}
        onChange={(e) => setSortOrder(e.target.value)}
        label="Sort Order"
      >
        <MenuItem value="asc">Ascending</MenuItem>
        <MenuItem value="desc">Descending</MenuItem>
      </Select>
    </FormControl>
  </div> */}
 
        </div>

        {/* Product Grid */}
        <div className="row gy-4 g-12">
          {returnVendorProducts.map((item) => {
            const rvp = item.product;

             const finalPrice = rvp?.price - rvp?.discount;
            return (
              <div key={rvp?.id} className="col-xxl-2 col-lg-3 col-sm-4 col-6">
                <div className="product-card px-8 py-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2">
                
                  <button
                              type="button"
                              className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                              disabled={isDeleting} // Disable button while deleting any product
                              onClick={() => handleDelete(item.id)} // Delete handler
                            >
                              {isDeleting ? (
                                <>
                                  <i className="ph ph-spinner text-2xl d-flex animate-spin" />
                                  Removing...
                                </>
                              ) : (
                                <>
                                  <i className="ph ph-x-circle text-2xl d-flex" />
                                  Remove
                                </>
                              )}
                 </button>

                  <div className="product-card__thumb flex-center">
                    <NavLink
                      to="/wholesale-center-products-returned"
                      state={{ preserveScroll: true }}
                      onClick={() => dispatch(setSelectedProduct(rvp))}
                    >
                      <img
                        src={rvp?.images[0]}
                        alt={rvp?.name}
                        className="object-cover rounded-16"
                      />
                    </NavLink>
                  </div>
                  <div className="product-card__content mt-12">
                    <div className="product-card__price mb-16">
                      <span className="text-gray-400 text-md fw-semibold text-decoration-line-through">
                        ${rvp?.price?.toFixed(2)}
                      </span>
                      <span className="text-heading text-md fw-semibold">
                        ${finalPrice?.toFixed(2)} <span className="text-gray-500 fw-normal">/Qty</span>
                      </span>
                    </div>
                    <div className="flex-align gap-6">
                      <span className="text-xs fw-bold text-gray-600">{rvp?.ratings?.toFixed(1)}</span>
                      <span className="text-15 fw-bold text-warning-600 d-flex">
                        <i className="ph-fill ph-star" />
                      </span>
                      <span className="text-xs fw-bold text-gray-600">(17k)</span>
                    </div>
                    <h6 className="title text-lg fw-semibold mt-12 mb-8">
                      <div className="link text-line-2">{rvp?.name}</div>
                    </h6>
                    <div className="flex-align gap-4">
                      <span className="text-main-600 text-md d-flex">
                        <i className="ph-fill ph-storefront" />
                      </span>
                      <span className="text-gray-500 text-xs">My store</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination Controls */}
        <div className="pagination flex-align mt-8">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  backgroundColor: '#EF6036',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#EF6036' }
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReturnedProductList;